import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { IBudget, IPage } from 'common/src/types/interfaces';

import { deleteBudget, getBudgets } from 'portal/src/api/BudgetsService';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import useAuth from '../../auth/useAuth';
import useSnackbarHandler from '../../utils/Snackbar/useSnackbarHandler';
import DataTable from '../common/DataTable';
import FormDatePicker from '../common/forms/FormDatePicker';
import SplitButton from '../common/SplitButton';

const getDefaultFiscalStartMonth = (orgStartMonth) => {
  if (orgStartMonth) {
    const date = new Date();
    date.setMonth(orgStartMonth);
    return date;
  }

  return null;
};
const Budgets = () => {
  const { getUser, hasReadOnlyAccess } = useAuth();
  const {
    organization: { fiscalYearStartMonth: orgStartMonth },
  } = getUser();
  const navigate = useNavigate();
  const snackbarHandler = useSnackbarHandler();

  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState<Date>(
    getDefaultFiscalStartMonth(orgStartMonth),
  );

  const {
    data: budgets,
    isError,
    isPending,
  } = useQuery({
    queryKey: ['budgets'],
    queryFn: async () => await getBudgets(),
    initialData: { data: [] } as IPage<IBudget>,
  });

  useEffect(() => {
    if (isError) {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  }, [isError, snackbarHandler]);

  const gridColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'fiscalYear',
      headerName: 'Budget Fiscal Year',
      flex: 1,
    },
    {
      field: 'total',
      headerName: 'Total Budgeted Amount',
      flex: 1,
      valueFormatter: (value) => numberFormatter(value),
    },
  ];

  const handleNavigate = (location: string, params = '') =>
    navigate(`./${location}?month=${fiscalYearStartMonth.getMonth()}${params}`);

  return (
    <DataTable
      actionsConfig={{
        extraActions: ({ id }) => [
          {
            disabled: hasReadOnlyAccess(),
            icon: <ContentCopyOutlinedIcon />,
            label: 'Copy',
            onClick: () => handleNavigate(id, '&copy=1'),
          },
        ],
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        deleteConfig: {
          targetStr: 'budget',
          onDeleteClick: async (id) => await deleteBudget(id),
          snackbarMsgKey: 'BUDGET',
        },
      }}
      columns={gridColumns}
      contextName={NESTED_ROUTES.ADMINISTRATION.BUDGETS}
      dataFetchError={isError}
      loading={isPending}
      rows={budgets.data}
      toolbarSlotProps={{
        children: (
          <>
            <FormDatePicker
              name="fiscalYearStartMonth"
              label="Fiscal Year First Month"
              placeholder=""
              size="small"
              value={fiscalYearStartMonth}
              onChange={(value) => setFiscalYearStartMonth(value)}
              other={{
                format: 'MMMM',
                views: ['month'],
                openTo: 'month',
              }}
              slotProps={{
                calendarHeader: { sx: { display: 'none' } },
                openPickerIcon: { sx: { width: 18, height: 18 } },
                textField: {
                  sx: {
                    '& .MuiPickersInputBase-sectionsContainer': {
                      justifyContent: 'center',
                    },
                  },
                  InputProps: { sx: { width: 150, height: 32 } },
                },
              }}
            />
            <SplitButton
              disabled={hasReadOnlyAccess()}
              name="New"
              endIcon={<NoteAddOutlinedIcon />}
              onClick={() => handleNavigate(NEW_ID)}
              options={[
                {
                  name: 'Copy from actual costs',
                  onClick: () => handleNavigate(NEW_ID, '&importfiscalyear=1'),
                  icon: <DifferenceOutlinedIcon />,
                },
              ]}
            />
          </>
        ),
        exportFileName: 'budgets',
        showFilters: true,
      }}
    />
  );
};

export default Budgets;
